/* eslint-disable camelcase */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import styled from 'styled-components';
import { formatNumber } from '../../../utils/numberFormat';

interface MilestoneData {
  progress_color_raw: string; // '1': Above Plan, '2': On Plan, '3': Medium Risk, '4': At Risk
  achieveTarget: number;
  expectedValue: number;
  progress: number;
  progress_type: string; // '1': Number, '2': Currency, '3': Percentage
  expectedProgress: number;
  initial_data: string;
  target: number;
  metricDaysLeft: number;
  counting: string;
  totalTargetTextDisplay: number;
  totalTargetText: number;
  targetValue: number;
  metricProgress: number;
  initialData: string;
}

interface KrHoverMilestonesProps {
  data: MilestoneData;
}

const isFalsey = (value: number | string | null | undefined) =>
  value === null || value === undefined || value === '';

const round = (value: number | string, precision = 0) => {
  // If the value is a string and contains a K, M, B, or T, return the value as is.
  // This is to handle the case where the value is a string and contains a K, M, B, or T.
  if (typeof value === 'string' && /[KMBT]$/i.test(value.trim())) return value;

  if (precision === 0) {
    return Number(value).toFixed(0);
  }
  return Math.round(Number(value) * 10 ** precision) / 10 ** precision;
};

const numberToUnit = (value: number | string| null | undefined) => {
  if (isFalsey(value)) return '--';
  return formatNumber(value);
}

const statusConfig = {
  1: { label: 'Above Plan', color: '#16742B' },
  2: { label: 'On Plan', color: '#389E0D' },
  3: { label: 'Medium Risk', color: '#E67307' },
  4: { label: 'At Risk', color: '#CF1322' },
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    padding: '16px 20px',
    maxWidth: '460px',
  },
  header: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '8px',
  },
  content: {
    borderLeft: '3px solid #BCD5E6',
    paddingLeft: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  row: {
    fontSize: '14px',
    color: '#262626',
    lineHeight: '22px',
  },
};

const getCountingText = (counting: string, t: TFunction) => {
  switch (counting) {
    case '1':
      return t('Should increase to');
    case '2':
      return t('Should decrease to');
    default:
      return '';
  }
}

const StyledContainer = styled('div')({
  textAlign: 'left',
});

export default function KrHoverMilestones({ data }: KrHoverMilestonesProps) {
  const { t } = useTranslation();

  const {
    progress_color_raw,
    achieveTarget,
    expectedValue,
    expectedProgress,
    initialData,
    targetValue,
    metricDaysLeft,
    metricProgress,
    counting,
  } = data;
  const status = statusConfig[Number(progress_color_raw)] ?? {
    label: '',
    color: '#000000',
  };

  return (
    <StyledContainer className="kr-hover-milestones">
      <div style={{ ...styles.header, color: status.color }}>
        {t(status.label)}
      </div>
      <div style={styles.content}>
        <div style={styles.row}>
          <span>{t('Actual')} </span>
          <strong>{numberToUnit(achieveTarget)}</strong>
          <span> , {t('Expected')} </span>
          <strong>{numberToUnit(expectedValue)}</strong>
        </div>
        <div style={styles.row}>
          <span>{t('Progress')} </span>
          <strong>{metricProgress ? round(metricProgress) : 0}%</strong>
          <span> , {t('Expected progress')} </span>
          <strong>{expectedProgress ? round(expectedProgress) : '--'}%</strong>
        </div>
        <div style={styles.row}>
          <strong>{numberToUnit(initialData)}</strong>
          <span> {getCountingText(counting, t)} </span>
          <strong>{numberToUnit(targetValue)}</strong>
          <span> ({metricDaysLeft} {t('days left')})</span>
        </div>
      </div>
    </StyledContainer>
  );
}
